'use client';

import { Box, Breadcrumbs as MuiBreadcrumbs, SxProps } from '@mui/material';
import { usePathname } from 'next/navigation';
import { NavigateNext } from '@mui/icons-material';
import Link from 'next/link';
import React, { useContext, useMemo } from 'react';

import { ServicesLayoutContext } from '@/page-components/Services/ServicesLayout/ServicesLayout';
import { Vendor } from '@/lib/types/interfaces/Vendor';
import { formatBreadcrumbText, handleGTMEvent, moveBetweenServicesAndLength2ToEnd } from '@/lib/utils/functions';

interface Props { children?: React.ReactNode, sx?: SxProps, showLinks?: boolean, service?: Vendor | null}

const BreadCrumbs = ({
  children, sx, showLinks = true, service = null,
}: Props) => {
  const pathname = usePathname();
  const { currentCategory, location } = useContext(ServicesLayoutContext);
  const pathNameList = pathname.split('/').filter(Boolean);
  const visiblePathNameList = moveBetweenServicesAndLength2ToEnd(pathNameList);

  const renderLinks = useMemo(() => visiblePathNameList.map((item, index) => {
    const link = `/${
      index < 3
        ? visiblePathNameList.slice(0, index + 1).join("/")
        : pathNameList
            .filter((it) => it !== visiblePathNameList[index + 1])
            .slice(0, index + 1)
            .join("/")
    }`;
    const visibleItem = visiblePathNameList[index];

    if (visibleItem === '') {
      return (
        <Box
          key='home-page-crumb'
          onClick={() => handleGTMEvent('/', 'click_on_breadcrumbs')}
        >
          <Link
            href='/'
            aria-label='Get back to main page'
            key='homepage'
            prefetch={false}
          >
            Home
          </Link>
        </Box>
      );
    }
    if (service && visibleItem === service.slug) {
      return (
        <Box
          key='slug-crumb'
          onClick={() => handleGTMEvent(link, 'click_on_breadcrumbs')}
        >
          <Link
            href={link}
            key={visibleItem}
            className='capitalize-first-letter'
            aria-label='Get back to main page'
            prefetch={false}
          >
            {formatBreadcrumbText(service.business_name)}
          </Link>
        </Box>
      );
    }

    if (currentCategory && visibleItem === currentCategory.slug) {
      return (
        <Box
          key='category-crumb'
          onClick={() => handleGTMEvent(link, 'click_on_breadcrumbs')}
        >
          <Link
            href={link}
            key={visibleItem}
            className='capitalize-first-letter'
            aria-label='Get back to main page'
            prefetch={false}
          >
            {formatBreadcrumbText(currentCategory.name)}
          </Link>
        </Box>
      );
    }

    if (visibleItem === 'service') {
      return (
        <Box
          key='service-crumb'
          onClick={() => handleGTMEvent('/services', 'click_on_breadcrumbs')}
        >
          <Link
            href='/services'
            aria-label='Get back to services page'
            key='services'
            prefetch={false}
          >
            Services
          </Link>
        </Box>
      );
    }

    return (
      <Box
        key='crumb'
        onClick={() => handleGTMEvent(link, 'click_on_breadcrumbs')}
      >
        <Link
          href={link}
          key={visibleItem}
          className='capitalize-first-letter'
          aria-label='Get back to main page'
          prefetch={false}
        >
          {
            visibleItem === location?.name 
              ? visibleItem 
              : decodeURIComponent(formatBreadcrumbText(visibleItem))
          }
        </Link>
      </Box>
    );
  }), [currentCategory, location]);

  return (
    <Box
      sx={sx}
    >
      { showLinks
        && (
        <MuiBreadcrumbs
          separator={<NavigateNext fontSize='small' />}
          aria-label='breadcrumb'
        >
          {renderLinks}
        </MuiBreadcrumbs>
        )}
      {children}
    </Box>
  );
};

export default BreadCrumbs;
